import { isFreeEnlargementLine } from "src/utils/free-enlargement";

/**
 * Identifying the photo group that add-order-photos just created.
 *
 * The endpoint answers with the WHOLE order rather than the group it made, so
 * the client has to work out which one is new. It did that by diffing against
 * `order?.orderPhotos || []` -- the React Query cache -- and that `|| []` is the
 * defect this module exists to remove.
 *
 * `order` comes from useOrderQuery(mainOrderId, guestToken), which is
 * `enabled: Boolean(orderId && guestToken)` on the CONTEXT pair. The upload
 * path, meanwhile, resolves its order through resolveOrderSession over four
 * stores -- context, URL, cookie and localStorage. Those two disagree whenever
 * the session survives somewhere the provider does not read (its mount restore
 * consults the URL and the cookie only, never localStorage), which on mobile is
 * routine: an evicted cookie is enough.
 *
 * In that state `order` is undefined, the baseline collapses to `[]`, and
 * `find(op => !empty.has(op.id))` returns orderPhotos[0] -- the OLDEST group.
 * The photos are then registered against the first group and the group that was
 * just created stays empty, forever. Nothing throws, so nothing is reported and
 * the customer is told nothing.
 *
 * The rule here: an empty baseline and an UNKNOWN baseline are different
 * answers and must not be spelled the same way. Pass `null` for "we do not
 * know"; pass `[]` only when the order genuinely had no groups.
 */

/**
 * How much the answer can be trusted.
 *
 * - `exact`     -- diffed against a known baseline; this id is the new group.
 * - `degraded`  -- no baseline available, so this is the best guess (the last
 *                  group in the response). Usable, but worth reporting.
 * - `none`      -- cannot be determined. The caller must NOT upload against a
 *                  guess, and must not delete anything either: it does not know
 *                  which group is which.
 */
export type NewOrderPhotoConfidence = "exact" | "degraded" | "none";

export interface NewOrderPhotoPick {
  id: string | null;
  confidence: NewOrderPhotoConfidence;
}

interface GroupLike {
  id?: string | null;
  isFreeEnlargement?: boolean;
}

// Never the free enlargement: repricing may add it between the baseline and
// this call, and the backend refuses uploads into it.
function usableIds(groups: readonly GroupLike[] | null | undefined): string[] {
  if (!Array.isArray(groups)) return [];
  return groups
    .filter((group) => !isFreeEnlargementLine(group))
    .map((group) => group?.id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

/**
 * Which group in `after` was created by the call we just made.
 *
 * @param knownBeforeIds ids the order held BEFORE the call, or `null`/`undefined`
 *   when that is genuinely unknown. An empty ARRAY means "known to have held
 *   none" and is treated as a real baseline.
 * @param after the groups on the order as returned by the call.
 */
export function pickNewOrderPhotoId(
  knownBeforeIds: readonly string[] | null | undefined,
  after: readonly GroupLike[] | null | undefined,
): NewOrderPhotoPick {
  const afterIds = usableIds(after);
  if (afterIds.length === 0) return { id: null, confidence: "none" };

  if (!Array.isArray(knownBeforeIds)) {
    // No baseline. The newly created group is appended, so the last entry is
    // the best available guess -- and strictly better than the first, which is
    // the oldest group and the one this bug kept picking.
    return { id: afterIds[afterIds.length - 1], confidence: "degraded" };
  }

  const before = new Set(knownBeforeIds);
  const added = afterIds.filter((id) => !before.has(id));
  if (added.length === 0) {
    // The response contains nothing the baseline did not already have. Either
    // the group was not created or the response is stale; either way we cannot
    // name it, and attaching photos to a group we picked at random is exactly
    // the failure being fixed.
    return { id: null, confidence: "none" };
  }
  // More than one added means the baseline was behind (e.g. another tab added a
  // group). The last one is still ours -- it was appended by this call.
  return { id: added[added.length - 1], confidence: "exact" };
}
